import { SocketIOGeneralRoomProvider } from '@fabric-app/business/socket.io/GeneralRoom/SocketIOProvider';
import { AnonShareTokenContext } from '@fabric-app/business/permission/contexts/AnonShareTokenContext';
import type { ResourceDetail } from '@fabric-app/types/resources';
import type { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { woodyClient } from '@/src/services/woody/WoodyProvider';
import { getRouteParamAsString } from '@fabric-app/utils/url/routing';
import { getResourceNamePlaceholder } from '@fabric-app/business/resources/utils/getResourceTitle';
import { useQueryResourceDetail } from '@fabric-app/business/resources/queries/useQueryResourceDetail';
import { ResourceDataContextProvider } from '@fabric-app/business/resource-detail/components/context/ResourceDataContextProvider';
import type { ApiResourcePublishing } from '@fabric-app/types/publishing/publishing.types';
import { SocketIOParentRoomProvider } from '@fabric-app/business/socket.io/ParentRoom/SocketIOParentRoomProvider';
import { ResourceRoomProvider } from '@fabric-app/business/multiplayer/config/fdocs.config';
import { useMemo } from 'react';
import { getResourceCaption } from '@fabric-app/business/resources/utils/getResourceCaption';
import ExpandedFdoc from '@fabric-app/business/resource-detail/components/ExpandedFdoc/ExpandedFdoc';
import { defaultModalContentProps, Modal } from '@fabric-app/ui/components/Modal/Modal';
import { ResourceViewMobile } from '@fabric-app/business/resource-detail/components/ExpandedResource/MobileView/ResourceViewMobile';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { noop } from '@fabric-app/utils/guards/noop';
import { DialogContent } from '@radix-ui/react-dialog';
import { styled } from 'styled-components';
import { Kind } from '@fabric/woody-client';
import { getResourceDimensions } from '@fabric-app/business/resources/utils/getResourceDimensions';
import { ExpandedFdoc_OLD } from '@/src/hooks/useExpandedFdoc';
import { ModalResource } from '@fabric-app/business/resource-detail/components/ExpandedResource/ModalResource';

type Props = {
  slug: string;
  accessToken: string;
  preloadedResource: ResourceDetail;
  publishingData: ApiResourcePublishing;
  title: string;
  description: string;
  url: string;
  image: {
    url: string;
    width: number;
    height: number;
  };
  baseURL: string;
};

/**
 * we're rendering resource.thumbnail?.lg as og:image
 * lg thumbnail has longest edge of size 1080px
 */
const MAX_OG_IMAGE_LONG_EDGE = 1080;

/**
 * scales down the dimension to ensure the longest edge is MAX_OG_IMAGE_LONG_EDGE for og:image:width and og:image:height
 */
const scaleDownDimensions = ({ width, height }: { width: number; height: number }) => {
  if (width > height) {
    if (width > MAX_OG_IMAGE_LONG_EDGE) {
      return {
        width: MAX_OG_IMAGE_LONG_EDGE,
        height: Math.round(height * (MAX_OG_IMAGE_LONG_EDGE / width)),
      };
    }
  } else {
    return {
      width: Math.round(width * (MAX_OG_IMAGE_LONG_EDGE / height)),
      height: MAX_OG_IMAGE_LONG_EDGE,
    };
  }
};

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  const [slugAndId] = getRouteParamAsString(context.query, ['slugAndId']);

  if (!slugAndId) {
    return {
      notFound: true,
    };
  }

  const splittedSlugAndId = slugAndId.split('-');

  const slug = splittedSlugAndId.slice(0, -1).join('-');
  const accessToken = splittedSlugAndId.slice(-1)[0];

  const baseURL =
    process.env.NEXT_PUBLIC_WEBSITE_URL ?? `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`;
  const currentUrl = baseURL + '/p/' + slugAndId;

  const url = {
    href: currentUrl,
    origin: baseURL,
  };

  try {
    const publishingData = await woodyClient.v2({
      endpoint: '/v2/publish-token/{accessToken}',
      params: {
        accessToken,
      },
    });

    const resource = (await woodyClient.v2(
      {
        endpoint: '/v2/resources/{resourceId}',
        params: {
          resourceId: publishingData.resourceId,
        },
      },
      {
        query: {
          accessToken: accessToken,
        },
      },
    )) as ResourceDetail;

    const title = resource.name || getResourceNamePlaceholder(resource);

    const imageResourceDimensionsOriginal = getResourceDimensions(resource);
    const imageResourceDimensions = imageResourceDimensionsOriginal
      ? scaleDownDimensions(imageResourceDimensionsOriginal)
      : undefined;

    return {
      props: {
        slug,
        accessToken,
        baseURL,
        preloadedResource: JSON.parse(JSON.stringify(resource)),
        publishingData: JSON.parse(JSON.stringify(publishingData)),
        title: `${title} - on Fabric`,
        description:
          getResourceCaption(resource) ||
          `${resource.user?.name || 'Someone'} published ${title} on Fabric.`,
        url: url.href,
        image: resource.thumbnail?.lg
          ? {
              url: resource.thumbnail?.lg,
              ...(imageResourceDimensions
                ? imageResourceDimensions
                : {
                    width: MAX_OG_IMAGE_LONG_EDGE,
                    height: MAX_OG_IMAGE_LONG_EDGE,
                  }),
            }
          : {
              url: `${url.origin}/images/fabric-favicon-512.png`,
              width: 512,
              height: 512,
            },
      },
    };
  } catch {
    return {
      notFound: true,
    };
  }
};

export default function Page({
  accessToken,
  publishingData,
  preloadedResource,
  title,
  description,
  url,
  image,
  baseURL,
}: Props) {
  const { resource } = useQueryResourceDetail(
    {
      resourceId: preloadedResource.id,
      accessToken: accessToken,
      includeHidden: true,
    },
    {
      initialData: preloadedResource,
    },
  );

  const tokenData = useMemo(() => {
    return {
      type: 'publishing' as const,
      ...publishingData,
    };
  }, [publishingData]);

  const { isDesktopView } = useResponsive();

  return (
    <AnonShareTokenContext.Provider value={tokenData}>
      <SocketIOGeneralRoomProvider>
        <NextSeo
          title={title}
          description={description}
          noindex={!tokenData.allowIndex}
          nofollow={!tokenData.allowIndex}
          openGraph={{
            title,
            url,
            description,
            type: preloadedResource.kind === Kind.VIDEO ? 'video.other' : 'website',
            images: [image],
            site_name: 'Fabric',
          }}
          twitter={{
            handle: '@fabricinternet',
            site: '@fabricinternet',
            // not big card
            cardType: 'summary',
          }}
          additionalMetaTags={[
            {
              property: 'og:logo',
              content: `${baseURL}/images/fabric-favicon-512.png`,
            },
          ]}
        />

        <SocketIOParentRoomProvider
          stayConnectedWhenDocumentNotVisible
          room={{
            path: 'resources',
            params: {
              resourceId: preloadedResource.id,
            },
          }}
        >
          {isDesktopView ? <ExpandedFdoc_OLD /> : <ModalResource />}
          <ResourceRoomProvider resourceId={preloadedResource.id}>
            <ResourceDataContextProvider resource={resource || preloadedResource}>
              {!isDesktopView ? (
                <Modal.Root open={true}>
                  <ModalDialog {...defaultModalContentProps}>
                    <ResourceViewMobile resourceId={resource.id} isActive handleOnDelete={noop} />
                  </ModalDialog>
                </Modal.Root>
              ) : (
                <ExpandedFdoc
                  resourceId={resource.id}
                  hideBackdrop
                  goToNextResource={undefined}
                  goToPrevResource={undefined}
                  disablePortalRender
                  forceFullScreen
                />
              )}
            </ResourceDataContextProvider>
          </ResourceRoomProvider>
        </SocketIOParentRoomProvider>
      </SocketIOGeneralRoomProvider>
    </AnonShareTokenContext.Provider>
  );
}

const ModalDialog = styled(DialogContent)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 125;
  transition: none;
  overscroll-behavior-x: contain;
`;
